import {useState, useEffect, useRef} from 'react';

/**
 * Hook for handling the thumbnail tooltip.
 * @returns {object} - An object containing the following values:
 *   - ref: a reference to the thumbnail element in the DOM
 *   - tooltipVisible: a boolean indicating whether the tooltip should be visible
 *   - tooltipPosition: an object containing the top and left positions for the tooltip
 *   - handleMouseEnter: a function to handle the mouse enter event on the thumbnail
 *   - handleMouseLeave: a function to handle the mouse leave event on the thumbnail
 *
 *   note: not used
 */
export const useThumbnailTooltip = () => {
  const thumbnailRef = useRef(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(null);

  /** reference to the tooltip timeout (to clear it if necessary) */
  const tooltipTimeoutRef = useRef(null);

  const updateTooltipPosition = () => {
    const domRect = thumbnailRef.current && thumbnailRef.current.getBoundingClientRect() || undefined;

    // Calculate the center and top positions for the tooltip. We want the tooltip to be centered.
    const thumbnailCenter = domRect.left + domRect.width / 2;
    const thumbnailTop = domRect.bottom + 4;

    setTooltipPosition({top: thumbnailTop, left: thumbnailCenter});
  };

  useEffect(() => {
    // set a delay before setting the tooltip position (to allow for animations)
    if (thumbnailRef.current && tooltipVisible) {
      tooltipTimeoutRef.current = setTimeout(updateTooltipPosition, 200);
    }
    if (!tooltipVisible) {
      setTooltipPosition(null);
    }
  }, [tooltipVisible]);

  // handler for mouse enter event on the thumbnail
  const handleMouseEnter = () => {
    setTooltipVisible(true);
  };

  // handler for mouse leave event on the thumbnail
  const handleMouseLeave = () => {
    setTooltipVisible(false);
    // clear the timeout if one has been set
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
  };

  return {
    ref: thumbnailRef,
    tooltipVisible,
    tooltipPosition,
    handleMouseEnter,
    handleMouseLeave,
  };
};

/**
 * Hook for handling the image loading state.
 * @returns {object} - An object containing the following values:
 *   - isImageLoading: a boolean indicating whether the image is loading
 *   - handleImageLoad: a function to handle the image load event
 */
export const useIsImageLoading = () => {
  const [isLoading, setIsLoading] = useState(true);
  const handleImageLoad = () => {
    setIsLoading(false); // set loading to false when image is loaded
  };
  return {isImageLoading: isLoading, handleImageLoad};
};

/**
 * Hook for handling the carousel.
 *
 * @param {object} props - An object containing the following properties:
 *   - externalRef {React.RefObject<HTMLDivElement> | null}: This is an optional parameter,
 *     which should be a ref to an HTMLDivElement or null. It's used for testing purposes.
 *     When this ref is provided, the carousel will be based on the element this ref points to.
 *
 * @returns {object} - An object containing the following values:
 *   - isCarousel: a boolean indicating whether the thumbnails are in a carousel
 *   - carouselRef: a reference to the carousel element in the DOM
 *   - handleArrowClick: a function to handle the arrow click event
 *   - translateX: the current translateX value for the carousel
 *   - isLeftArrowVisible: a boolean indicating whether the left arrow should be visible
 *   - isRightArrowVisible: a boolean indicating whether the right arrow should be visible
 */
export const useCarousel = ({externalRef = null} = {}) => {
  const [isCarousel, setIsCarousel] = useState(false);
  const [thumbnailsWidth, setThumbnailsWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const carouselRef = useRef(null);
  const arrayLength = Array.from(carouselRef?.current?.children || []).length || 0;
  useEffect(() => {
    if (externalRef) {
      carouselRef.current = externalRef.current;
    }
  }, [externalRef]);

  useEffect(() => {
    const calcWidths = () => {
      if (!carouselRef.current) return;
      const containerW = carouselRef.current.offsetWidth;
      const thumbnailsW = Array.from(carouselRef.current.children).reduce((totalWidth, child) => totalWidth + child.offsetWidth, 0);
      setIsCarousel(thumbnailsW > containerW);
      setThumbnailsWidth(thumbnailsW);
      setContainerWidth(containerW);
    };

    calcWidths();
    window.addEventListener('resize', calcWidths);
    return () => window.removeEventListener('resize', calcWidths);
  }, [arrayLength]);

  const handleArrowClick = (direction) => {
    if (!carouselRef.current) return;

    // All of the thumbnails have the same width and margin, so we can just use the first one to calculate the scroll position.
    const thumbnail = carouselRef.current.children[0];
    const thumbnailStyle = window.getComputedStyle(thumbnail);
    const thumbnailMarginRight = parseFloat(thumbnailStyle.marginRight);
    const extraSpaceToClearEdges = 10;
    const scrollAmount = thumbnail.offsetWidth + thumbnailMarginRight + extraSpaceToClearEdges;

    const newTranslateX = direction === 'left' ? translateX + scrollAmount : translateX - scrollAmount;
    setTranslateX(newTranslateX);
  };

  const isLeftArrowVisible = isCarousel && translateX < 0;
  const isRightArrowVisible = isCarousel && Math.abs(translateX) < thumbnailsWidth - containerWidth;

  return {isCarousel, carouselRef, handleArrowClick, translateX, isLeftArrowVisible, isRightArrowVisible};
};

/**
 * Hook for setting up the tooltip portal.
 * @param {object} options - An object containing the following values:
 *   - portalId: the id to use for the tooltip portal
 * @returns {Element | null} - A reference to the tooltip portal element in the DOM.
 */
export const useTooltipSetup = ({portalId = 'thumbnail-tooltip-portal'} = {}) => {
  const [tooltipPortalElement, setTooltipPortalElement] = useState(null);

  useEffect(() => {
    const div = document.createElement('div');
    div.id = portalId;
    document.body.appendChild(div);
    setTooltipPortalElement(div);
    return () => {
      document.body.removeChild(div);
    };
  }, [portalId]);

  return tooltipPortalElement;
};
